import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

const listAccounts = httpsCallable(functions, 'listSupportAccounts');
const listAuditLogs = httpsCallable(functions, 'listSupportAuditLogs');
const listAnalytics = httpsCallable(functions, 'listFounderAnalytics');
const exportAnalytics = httpsCallable(functions, 'exportFounderAnalytics');
const updateEntitlement = httpsCallable(functions, 'updateSupportEntitlement');
const updateAccountStatus = httpsCallable(functions, 'updateAccountStatus');

export default function AdminDashboard({ onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [analytics, setAnalytics] = useState({ websiteDaily: [], business: {} });
  const [rangeDays, setRangeDays] = useState(30);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyUID, setBusyUID] = useState('');
  const [query, setQuery] = useState('');
  const [recovery, setRecovery] = useState(null);
  const [accountAction, setAccountAction] = useState(null);
  const liveAccounts = accounts.filter((account) => account.plan === 'live');
  const routeCalls = accounts.reduce((total, account) => total + (account.routeCallsThisMonth || 0), 0);
  const estimatedRouteCost = accounts.reduce((total, account) => total + (account.estimatedRouteCostThisMonth || 0), 0);
  const estimatedGrossRevenue = accounts.reduce((total, account) => total + (account.estimatedMonthlyGrossRevenue || 0), 0);
  const estimatedPlatformProceeds = accounts.reduce((total, account) => total + (account.estimatedMonthlyPlatformProceeds || 0), 0);
  const estimatedContribution = estimatedPlatformProceeds - estimatedRouteCost;
  const supportOverrides = accounts.filter((account) => account.entitlementSource === 'support_override');
  const eventTotal = (name) => analytics.websiteDaily.reduce((total, day) => total + Number(day.eventCounts?.[name] || 0), 0);
  const websiteSessions = analytics.websiteDaily.reduce((total, day) => total + Number(day.uniqueSessions || 0), 0);
  const pageViews = eventTotal('page_view') + eventTotal('privacy_viewed');
  const pricingInterest = eventTotal('plan_selected');
  const checkoutStarts = eventTotal('checkout_started');
  const checkoutSuccesses = eventTotal('checkout_succeeded');
  const visitorToCheckout = websiteSessions ? (checkoutStarts / websiteSessions) * 100 : 0;
  const checkoutConversion = checkoutStarts ? (checkoutSuccesses / checkoutStarts) * 100 : 0;
  const sources = Object.entries(analytics.websiteDaily.reduce((result, day) => {
    Object.entries(day.sourceCounts || {}).forEach(([source, count]) => { result[source] = (result[source] || 0) + Number(count); });
    return result;
  }, {})).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const expiringSoon = supportOverrides.filter((account) => {
    if (!account.entitlementExpiresAt) return false;
    const remaining = new Date(account.entitlementExpiresAt).getTime() - Date.now();
    return remaining > 0 && remaining <= 7 * 24 * 60 * 60 * 1000;
  });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [accountResult, auditResult, analyticsResult] = await Promise.all([listAccounts(), listAuditLogs(), listAnalytics({ days: rangeDays })]);
      setAccounts(accountResult.data);
      setAuditLogs(auditResult.data);
      setAnalytics(analyticsResult.data);
    }
    catch (requestError) { setError(requestError.message || 'Could not load support accounts.'); }
    finally { setLoading(false); }
  }, [rangeDays]);

  async function downloadAIExport(format) {
    setExporting(true); setError('');
    try {
      const result = await exportAnalytics({ days: rangeDays });
      const packageData = {
        schemaVersion: 1,
        generatedAt: new Date().toISOString(),
        purpose: 'LeaveNOW founder operating analysis',
        privacy: result.data.privacy,
        summary: analytics,
        operatingEstimates: {
          grossMonthlyRevenue: estimatedGrossRevenue,
          estimatedPlatformProceeds,
          estimatedRouteCost,
          estimatedContribution
        },
        websiteEvents: result.data.events
      };
      const date = new Date().toISOString().slice(0, 10);
      if (format === 'csv') {
        const headers = ['timestamp', 'event', 'page', 'source', 'campaign', 'device', 'session', 'authenticated', 'properties'];
        const escape = (value) => `"${String(value ?? '').replaceAll('"', '""')}"`;
        const rows = result.data.events.map((event) => headers.map((header) => escape(header === 'properties' ? JSON.stringify(event.properties) : event[header])).join(','));
        downloadFile(`leavenow-website-analytics-${date}.csv`, [headers.join(','), ...rows].join('\n'), 'text/csv');
      } else {
        downloadFile(`leavenow-founder-analysis-${date}.json`, JSON.stringify(packageData, null, 2), 'application/json');
      }
    } catch (requestError) {
      setError(requestError.message || 'Analytics export failed.');
    } finally { setExporting(false); }
  }

  useEffect(() => { load(); }, [load]);

  async function change({ account, action, days, reason }) {
    setBusyUID(account.uid); setError('');
    try {
      await updateEntitlement({ uid: account.uid, action, days, reason });
      setRecovery(null);
      await load();
    }
    catch (requestError) { setError(requestError.message || 'The support action failed.'); }
    finally { setBusyUID(''); }
  }

  async function changeAccountStatus({ account, reason }) {
    setBusyUID(account.uid); setError('');
    try {
      await updateAccountStatus({ uid: account.uid, disabled: !account.disabled, reason });
      setAccountAction(null);
      await load();
    }
    catch (requestError) { setError(requestError.message || 'The account status change failed.'); }
    finally { setBusyUID(''); }
  }

  const normalizedQuery = query.trim().toLowerCase();
  const visibleAccounts = normalizedQuery
    ? accounts.filter((account) => `${account.displayName || ''} ${account.email || ''} ${account.uid}`.toLowerCase().includes(normalizedQuery))
    : accounts;

  return (
    <main className="admin-dashboard">
      <div className="admin-toolbar">
        <div><p className="eyebrow">Restricted access</p><h1>Founder dashboard</h1></div>
        <button className="secondary-button" onClick={onClose}>Back to trips</button>
      </div>
      <p className="admin-note">Revenue and margin are operating estimates for fast decisions. App Store settlement reports remain the accounting source of truth.</p>
      {error && <p className="form-error" role="alert">{error}</p>}
      {loading ? <div className="app-loader admin-loading"><span /></div> : (<>
        <div className="analytics-toolbar">
          <div><p className="eyebrow">Operating window</p><h2>Website and business health</h2></div>
          <div>
            <select aria-label="Analytics date range" value={rangeDays} onChange={(event) => setRangeDays(Number(event.target.value))}>
              <option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option>
            </select>
            <button className="secondary-button" disabled={exporting} onClick={() => downloadAIExport('json')}>{exporting ? 'Preparing…' : 'Export for AI'}</button>
            <button className="secondary-button" disabled={exporting} onClick={() => downloadAIExport('csv')}>Export CSV</button>
          </div>
        </div>
        <section className="founder-metrics" aria-label="Customer access overview">
          <article><span>Website traffic</span><strong>{websiteSessions.toLocaleString()}</strong><small>Sessions · {pageViews.toLocaleString()} page views</small></article>
          <article><span>Pricing interest</span><strong>{pricingInterest.toLocaleString()}</strong><small>{visitorToCheckout.toFixed(1)}% visitor → checkout</small></article>
          <article className={checkoutStarts && !(analytics.business?.stripeSubscribers) ? 'attention' : ''}><span>Active web subscribers</span><strong>{analytics.business?.stripeSubscribers || 0}</strong><small>Verified by Stripe · {checkoutConversion.toFixed(1)}% returned successful</small></article>
          <article><span>Total accounts</span><strong>{accounts.length}</strong><small>Known billing records</small></article>
          <article><span>LeaveNOW Live</span><strong>{liveAccounts.length}</strong><small>{accounts.length ? `${Math.round((liveAccounts.length / accounts.length) * 100)}% of accounts` : 'No accounts yet'}</small></article>
          <article><span>Route calls</span><strong>{routeCalls.toLocaleString()}</strong><small>Current month</small></article>
          <article><span>Cost exposure</span><strong>${estimatedRouteCost.toFixed(2)}</strong><small>Pro list price before free tier</small></article>
          <article><span>Gross MRR</span><strong>${estimatedGrossRevenue.toFixed(2)}</strong><small>Monthly equivalent</small></article>
          <article className={estimatedContribution < 0 ? 'attention' : ''}><span>Contribution</span><strong>${estimatedContribution.toFixed(2)}</strong><small>Est. after platform fees and route cost</small></article>
          <article className={expiringSoon.length ? 'attention' : ''}><span>Expiring soon</span><strong>{expiringSoon.length}</strong><small>Within seven days</small></article>
        </section>
        <section className="analytics-grid">
          <article>
            <p className="eyebrow">Acquisition</p><h2>Traffic sources</h2>
            {sources.length ? <ol className="source-list">{sources.map(([source, count]) => <li key={source}><span>{source.replaceAll('_', ' ')}</span><strong>{count.toLocaleString()}</strong></li>)}</ol> : <p className="admin-note">Tracking starts with this deployment. No source data yet.</p>}
          </article>
          <article>
            <p className="eyebrow">Conversion</p><h2>Website funnel</h2>
            <div className="funnel-list"><div><span>Sessions</span><strong>{websiteSessions}</strong></div><div><span>Plan selections</span><strong>{pricingInterest}</strong></div><div><span>Checkout starts</span><strong>{checkoutStarts}</strong></div><div><span>Success-page returns</span><strong>{checkoutSuccesses}</strong></div></div>
          </article>
          <article>
            <p className="eyebrow">Product</p><h2>Trip health</h2>
            <div className="funnel-list"><div><span>Total trips</span><strong>{analytics.business?.trips || 0}</strong></div><div><span>Active/scheduled</span><strong>{analytics.business?.scheduledTrips || 0}</strong></div><div><span>Archived</span><strong>{analytics.business?.archivedTrips || 0}</strong></div><div><span>Completed</span><strong>{analytics.business?.completedTrips || 0}</strong></div></div>
          </article>
        </section>
        <div className="admin-section-heading"><div><p className="eyebrow">Customer recovery</p><h2>Accounts and entitlements</h2></div><button className="secondary-button" onClick={load}>Refresh</button></div>
        <label className="admin-search">
          <span>Find a customer</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Email, name, or account ID" autoComplete="off" />
        </label>
        <div className="admin-table-wrap"><table className="admin-table">
          <thead><tr><th>User</th><th>Plan</th><th>Revenue</th><th>Usage / cost</th><th>Source</th><th>Expires</th><th>Actions</th></tr></thead>
          <tbody>{visibleAccounts.length ? visibleAccounts.map((account) => <tr key={account.uid}>
            <td><strong>{account.displayName || 'Unnamed user'}</strong><small>{account.email || account.uid}</small></td>
            <td><span className={`plan-badge ${account.plan}`}>{account.plan}</span></td>
            <td>${(account.estimatedMonthlyGrossRevenue || 0).toFixed(2)}<small>monthly equivalent</small></td>
            <td>{(account.routeCallsThisMonth || 0).toLocaleString()} calls<small>${(account.estimatedRouteCostThisMonth || 0).toFixed(2)}</small></td>
            <td>{account.entitlementSource}</td>
            <td>{account.entitlementPermanent ? 'Indefinite' : account.entitlementExpiresAt ? new Date(account.entitlementExpiresAt).toLocaleString() : '—'}</td>
            <td><div className="admin-actions">{account.entitlementSource === 'support_override'
              ? <button disabled={busyUID === account.uid} onClick={() => setRecovery({ account, action: 'revoke' })}>Revoke</button>
              : <button disabled={busyUID === account.uid || account.entitlementSource === 'app_store'} onClick={() => setRecovery({ account, action: 'grant' })}>Grant Live</button>}
              <button className={account.disabled ? '' : 'danger-button'} disabled={busyUID === account.uid} onClick={() => setAccountAction({ account })}>{account.disabled ? 'Reactivate' : 'Deactivate'}</button>
            </div></td>
          </tr>) : <tr><td colSpan="7" className="admin-empty">No matching customer accounts.</td></tr>}</tbody>
        </table></div>
        <section className="audit-section">
          <div className="admin-section-heading"><div><p className="eyebrow">Accountability</p><h2>Recent support actions</h2></div></div>
          <div className="admin-table-wrap"><table className="admin-table">
            <thead><tr><th>When</th><th>Action</th><th>Founder</th><th>Reason</th></tr></thead>
            <tbody>{auditLogs.length ? auditLogs.map((log) => <tr key={log.id}>
              <td>{log.createdAt ? new Date(log.createdAt).toLocaleString() : 'Pending'}</td>
              <td>{log.action.replaceAll('_', ' ')}</td>
              <td>{log.actor}</td>
              <td>{log.reason}{log.days ? ` (${log.days} days)` : ''}</td>
            </tr>) : <tr><td colSpan="4" className="admin-empty">No support actions recorded.</td></tr>}</tbody>
          </table></div>
        </section>
      </>)}
      {recovery && <RecoveryDialog recovery={recovery} busy={busyUID === recovery.account.uid} onCancel={() => setRecovery(null)} onConfirm={change} />}
      {accountAction && <AccountStatusDialog action={accountAction} busy={busyUID === accountAction.account.uid} onCancel={() => setAccountAction(null)} onConfirm={changeAccountStatus} />}
    </main>
  );
}

function downloadFile(name, contents, type) {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const link = document.createElement('a');
  link.href = url; link.download = name; link.click();
  URL.revokeObjectURL(url);
}

function RecoveryDialog({ recovery, busy, onCancel, onConfirm }) {
  const [days, setDays] = useState('30');
  const [reason, setReason] = useState('');
  const granting = recovery.action === 'grant';
  const valid = reason.trim().length >= 8;

  function submit(event) {
    event.preventDefault();
    if (valid) onConfirm({ ...recovery, days: granting ? (days === 'indefinite' ? null : Number(days)) : undefined, reason: reason.trim() });
  }

  return <div className="recovery-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
    <form className="recovery-dialog" role="dialog" aria-modal="true" aria-labelledby="recovery-title" onSubmit={submit}>
      <p className="eyebrow">Audited support action</p>
      <h2 id="recovery-title">{granting ? 'Authorize temporary Live access' : 'Revoke recovery access'}</h2>
      <p>{recovery.account.email || recovery.account.uid}</p>
      {granting && <label><span>Live access duration</span><select value={days} onChange={(event) => setDays(event.target.value)}><option value="30">1 month</option><option value="90">3 months</option><option value="180">6 months</option><option value="365">1 year</option><option value="indefinite">Indefinite</option><option value="7">7 days</option><option value="1">1 day</option></select></label>}
      <label><span>Support reason</span><textarea value={reason} onChange={(event) => setReason(event.target.value)} minLength="8" maxLength="500" placeholder="Ticket or customer issue (required)" autoFocus /></label>
      <small>This does not alter or refund an App Store subscription. The action is recorded permanently.</small>
      <div><button type="button" className="secondary-button" onClick={onCancel}>Cancel</button><button type="submit" className="primary-button" disabled={!valid || busy}>{busy ? 'Saving…' : granting ? 'Authorize recovery' : 'Revoke access'}</button></div>
    </form>
  </div>;
}

function AccountStatusDialog({ action, busy, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const deactivating = !action.account.disabled;
  const valid = reason.trim().length >= 8;
  return <div className="recovery-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
    <form className="recovery-dialog" role="dialog" aria-modal="true" aria-labelledby="status-title" onSubmit={(event) => { event.preventDefault(); if (valid) onConfirm({ ...action, reason: reason.trim() }); }}>
      <p className="eyebrow">Audited account action</p>
      <h2 id="status-title">{deactivating ? 'Deactivate account?' : 'Reactivate account?'}</h2>
      <p>{action.account.email || action.account.uid}</p>
      <label><span>Reason</span><textarea value={reason} onChange={(event) => setReason(event.target.value)} minLength="8" maxLength="500" placeholder="Support ticket, fraud concern, or customer request" autoFocus /></label>
      <small>{deactivating ? 'The customer will be unable to sign in. This does not cancel or refund an App Store subscription.' : 'The customer will be able to sign in again.'}</small>
      <div><button type="button" className="secondary-button" onClick={onCancel}>Cancel</button><button type="submit" className="primary-button" disabled={!valid || busy}>{busy ? 'Saving…' : deactivating ? 'Deactivate' : 'Reactivate'}</button></div>
    </form>
  </div>;
}
